import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  FormSectionLabel,
  LoadingState,
  Page,
  ResponsiveGrid,
  Text,
} from "@marekl/adminer/components";
import { pluralizeCzech } from "@marekl/adminer/format";
import { AppTopbar } from "../../../shell/AppTopbar";
import { useSeason } from "../../../season";
import { useCompetitions, type Competition } from "../../../lib/queries/competitions";
import { useTeamEntries, type Entry } from "../../../lib/queries/entries";
import { useTeamMembers } from "../../../lib/queries/memberships";
import { useTeam } from "../../../lib/queries/teams";
import { RowLink } from "../../../components/RowLink";
import { BadgeRow } from "../../../components/BadgeRow";
import { CompetitionDeadlines } from "../../../components/CompetitionDeadlines";
import { SeasonRegistrationNotice } from "../../../components/SeasonRegistrationNotice";
import {
  clubCompetitionBadge,
  COMPETITION_TYPE_LABELS,
  formatCompetitionDates,
} from "../../../lib/competitionState";
import { musicMissingCount } from "../../../lib/musicState";
import {
  hasRegisteredMember,
  isMcr,
  clubEntryBlock,
} from "../../../lib/entryEligibility";
import styles from "./TeamCompetitionsPage.module.css";
import type { ID } from "../../../lib/id";

// The club's season at a glance: every competition at once, upcoming ones
// as cards answering „kdy, do kdy a jak na tom jsme", past ones below.
//
// It replaced three tabs (Otevřené / Naše přihlášky / Proběhlé). A leader
// had to find the right tab before seeing anything, and a competition that
// was running, or published with its date already behind it, sat in none
// of them.

function isPast(c: Competition, today: string): boolean {
  return c.status === "finished" || c.status === "archived" || (c.date_to ?? c.date) < today;
}

// Odhlášky are a record, not a start, so they are not counted.
function submittedCount(entries: Entry[]): number {
  return entries.filter((e) => e.status === "submitted").length;
}

function entriesLabel(n: number, none: string): string {
  if (n === 0) return none;
  return `${n} ${pluralizeCzech(n, ["přihláška", "přihlášky", "přihlášek"])}`;
}

export function TeamCompetitionsPage() {
  const { id } = useParams();
  const teamId = id ?? "";
  const { activeSeason, isLoading: seasonLoading } = useSeason();
  // Gated on the season: without a season_id the endpoint answers with
  // every season's competitions.
  const competitions = useCompetitions(
    { season_id: activeSeason?.id },
    { enabled: !!activeSeason },
  );
  const teamEntries = useTeamEntries(teamId);
  const entries = useMemo(() => teamEntries.data ?? [], [teamEntries.data]);
  // The roster, not `team.state_issues`: the wizard asks the roster, and
  // the server fills state_issues for active teams only.
  const roster = useTeamMembers(teamId);
  const { data: team, isLoading: teamLoading, error: teamError } = useTeam(teamId);

  const entriesByCompetition = useMemo(() => {
    const out = new Map<ID, Entry[]>();
    for (const e of entries) {
      const list = out.get(e.competition_id);
      if (list) list.push(e);
      else out.set(e.competition_id, [e]);
    }
    return out;
  }, [entries]);

  const { upcoming, past } = useMemo(() => {
    const today = dayjs().format("YYYY-MM-DD");
    const all = competitions.data ?? [];
    return {
      upcoming: all.filter((c) => !isPast(c, today)).sort((a, b) => a.date.localeCompare(b.date)),
      past: all.filter((c) => isPast(c, today)).sort((a, b) => b.date.localeCompare(a.date)),
    };
  }, [competitions.data]);

  // Until the roster AND the team load nobody knows whether the club may
  // enter, so the MČR „Nová přihláška" waits rather than flashing in and out.
  // The team half matters as much as the roster: `?? true` below reads an
  // unloaded team as ČNSMS, so for that moment a foreign club was told it
  // „vyžaduje registraci do sezóny" — a page that would refuse it — instead
  // of that MČR is not its competition at all.
  const teamKnown = !roster.isLoading && !teamLoading;
  const notRegistered = teamKnown && !hasRegisteredMember(roster.data ?? []);
  // A team outside ČNSMS is refused at MČR whatever its roster says, and no
  // registration would help — so the notice pointing at one must not show.
  const teamIsCmf = team?.federation_is_primary ?? true;

  const body = () => {
    // The entries too: without them every card claims „Zatím žádná" beside
    // a „Nová přihláška", which a club that has filed would believe.
    // The team too: a failed team fetch clears teamLoading with `team`
    // undefined, and `?? true` below then reads a club outside ČNSMS as ČNSMS —
    // telling it to register into a season that would refuse it.
    const error = competitions.error ?? teamEntries.error ?? teamError;
    if (error) {
      return (
        <ErrorState
          title="Soutěže se nepodařilo načíst"
          error={error}
          onRetry={() => {
            void competitions.refetch();
            void teamEntries.refetch();
          }}
        />
      );
    }
    if (seasonLoading || competitions.isLoading || teamEntries.isLoading) {
      return <LoadingState />;
    }
    if (!activeSeason) {
      return (
        <EmptyState
          title="Žádná aktivní sezóna"
          description="Soutěže se zobrazí, jakmile bude sezóna otevřená."
        />
      );
    }
    if (upcoming.length === 0 && past.length === 0) {
      return (
        <EmptyState
          title="Zatím žádné soutěže"
          description={`V sezóně ${activeSeason.code} zatím nejsou vypsané žádné soutěže.`}
        />
      );
    }
    return (
      <>
        <Page.SectionHeader title="Nadcházející soutěže" meta={String(upcoming.length)} />
        {upcoming.length === 0 ? (
          <EmptyState
            title="Žádná nadcházející soutěž"
            description="V této sezóně už nejsou vypsané žádné další soutěže."
          />
        ) : (
          <ResponsiveGrid columns={2}>
            {upcoming.map((c) => (
              <UpcomingCard
                key={c.id}
                competition={c}
                teamId={teamId}
                entries={entriesByCompetition.get(c.id) ?? []}
                teamKnown={teamKnown}
                teamIsCmf={teamIsCmf}
                notRegistered={notRegistered}
              />
            ))}
          </ResponsiveGrid>
        )}
        {past.length > 0 && (
          <>
            <Page.SectionHeader title="Proběhlé soutěže" meta={String(past.length)} />
            <ResponsiveGrid columns={3}>
              {past.map((c) => (
                <PastCard
                  key={c.id}
                  competition={c}
                  teamId={teamId}
                  entries={entriesByCompetition.get(c.id) ?? []}
                />
              ))}
            </ResponsiveGrid>
          </>
        )}
      </>
    );
  };

  return (
    <>
      <AppTopbar
        crumbs={[{ label: "Členové týmu", href: `/teams/${teamId}` }, { label: "Soutěže" }]}
      />
      <Page>
        <Page.Header
          title="Soutěže a přihlášky"
          subtitle={activeSeason ? `Sezóna ${activeSeason.code}` : undefined}
        />
        <Page.Content stack gap="lg">
          {/* Once, above the cards, rather than on each MČR card: it is a
              fact about the team, and the cards only say what it blocks. */}
          {teamKnown && teamIsCmf && notRegistered && upcoming.some((c) => isMcr(c.type)) && (
            <SeasonRegistrationNotice teamId={teamId} />
          )}
          {body()}
        </Page.Content>
      </Page>
    </>
  );
}

function UpcomingCard({
  competition: c,
  teamId,
  entries,
  teamKnown,
  teamIsCmf,
  notRegistered,
}: {
  competition: Competition;
  teamId: ID;
  entries: Entry[];
  teamKnown: boolean;
  teamIsCmf: boolean;
  notRegistered: boolean;
}) {
  const detail = `/teams/${teamId}/souteze/${c.id}`;
  const badge = clubCompetitionBadge(c);
  const ours = submittedCount(entries);
  const missingMusic = musicMissingCount(entries);
  const block = clubEntryBlock(c.type, teamIsCmf, !notRegistered);
  return (
    <Card className={styles.card}>
      <Card.Header
        title={<RowLink to={detail}>{c.name}</RowLink>}
        subtitle={COMPETITION_TYPE_LABELS[c.type]}
      />
      <Card.Content className={styles.grow}>
        <div className={styles.body}>
          <BadgeRow gap="md">
            <Text variant="muted">
              {[formatCompetitionDates(c), c.venue].filter(Boolean).join(" · ")}
            </Text>
            {badge && <Badge variant={badge.variant}>{badge.label}</Badge>}
          </BadgeRow>
          <div>
            <FormSectionLabel>Uzávěrky</FormSectionLabel>
            <CompetitionDeadlines competition={c} />
          </div>
          <div>
            <FormSectionLabel>Naše přihlášky</FormSectionLabel>
            <BadgeRow gap="md">
              <span>{entriesLabel(ours, "Zatím žádná")}</span>
              {missingMusic > 0 && <Badge variant="warn">Chybí hudba: {missingMusic}</Badge>}
            </BadgeRow>
          </div>
        </div>
      </Card.Content>
      <Card.Footer>
        <div className={styles.actions}>
          {/* Creating needs an open phase and, for MČR, somebody
              registered. A published start list also closes it; the list
              does not carry that, so the competition's page and the
              server have the last word. */}
          {c.entry_phase === "open" &&
            teamKnown &&
            (block !== null ? (
              // Which of the two blocks it is decides what the club can do
              // about it: a season registration fixes one, and nothing
              // fixes the other. Telling a club outside ČNSMS to register
              // into a ČNSMS season sends it to a page that refuses it.
              <Text variant="muted">
                {block === "not_our_competition"
                  ? "Jen pro týmy ČNSMS"
                  : "Vyžaduje registraci do sezóny"}
              </Text>
            ) : (
              <Link to={`${detail}/nova-prihlaska`}>
                <Button variant="primary">Nová přihláška</Button>
              </Link>
            ))}
          <Link to={detail}>
            <Button>Detail</Button>
          </Link>
        </div>
      </Card.Footer>
    </Card>
  );
}

// Výsledky join these cards once Modul 9 exists; there is no results page
// to link yet.
function PastCard({
  competition: c,
  teamId,
  entries,
}: {
  competition: Competition;
  teamId: ID;
  entries: Entry[];
}) {
  const detail = `/teams/${teamId}/souteze/${c.id}`;
  return (
    <Card className={styles.card}>
      <Card.Header
        title={<RowLink to={detail}>{c.name}</RowLink>}
        subtitle={COMPETITION_TYPE_LABELS[c.type]}
      />
      <Card.Content className={styles.grow}>
        <div className={styles.body}>
          <Text variant="muted">{formatCompetitionDates(c)}</Text>
          <span>{entriesLabel(submittedCount(entries), "Bez přihlášky")}</span>
        </div>
      </Card.Content>
      <Card.Footer>
        <div className={styles.actions}>
          <Link to={detail}>
            <Button>Detail</Button>
          </Link>
        </div>
      </Card.Footer>
    </Card>
  );
}
